import { Duration, Instant, nativeJs } from "@js-joda/core";
import { dateJsonFormat, durationJsonFormat } from "@redotech/json-extra/joda";
import {
  JsonFormat,
  arrayJsonFormat,
  nullableJsonFormat,
  numberJsonFormat,
  objectJsonFormat,
  stringJsonFormat,
  symbolJsonFormat,
  symbolUnionJsonFormat,
} from "@redotech/json/format";
import { endOfDay, startOfDay } from "date-fns";
/**
 * Product condition
 */
export type Condition =
  | Condition.DiscountCode
  | Condition.FulfillmentPeriod
  | Condition.ShippingWithin
  | Condition.OrderDate
  | Condition.ReturnDate
  | Condition.OrderWithin
  | Condition.ProductTag
  | Condition.CustomerTag
  | Condition.CustomerCountry
  | Condition.SalesChannel
  | Condition.Price;

export enum DateOption {
  BEFORE = "Before or on",
  AFTER = "After or on",
  BETWEEN = "Between",
}

export enum PriceOption {
  EQUALS = "Equals",
  LESS_THAN = "Less than",
  GREATER_THAN = "Greater than",
  CENTS_EQUALS = "Cents equals",
}

import { Channel, SalesChannels } from "../sales-channels";

export namespace Condition {
  export const CUSTOMER_TAG = Symbol("customer_tag");
  export const DELIVERY_WITHIN = Symbol("shipping_within");
  export const DISCOUNT_CODE = Symbol("discount_code");
  export const FULFILLMENT_WITHIN = Symbol("fulfilment_within");
  export const ORDER_DATE = Symbol("order_date");
  export const RETURN_DATE = Symbol("return_date");
  export const ORDER_WITHIN = Symbol("order_within");
  export const PRICE = Symbol("price");
  export const PRODUCT_TAG = Symbol("product_tag");
  export const SALES_CHANNEL = Symbol("sales_channel");
  export const CUSTOMER_COUNTRY = Symbol("customer_country");

  export interface CustomerTag {
    type: typeof CUSTOMER_TAG;
    tags: readonly string[];
  }

  export const customerTagJsonFormat: JsonFormat<CustomerTag> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(CUSTOMER_TAG),
        tags: arrayJsonFormat(stringJsonFormat),
      },
      {},
    );

  export interface DiscountCode {
    type: typeof DISCOUNT_CODE;
    codes: readonly string[];
  }

  export const discountCodeJsonFormat: JsonFormat<DiscountCode> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(DISCOUNT_CODE),
        codes: arrayJsonFormat(stringJsonFormat),
      },
      {},
    );

  export interface FulfillmentPeriod {
    type: typeof FULFILLMENT_WITHIN;
    duration: Duration;
  }

  export const fulfillmentPeriodJsonFormat: JsonFormat<FulfillmentPeriod> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(FULFILLMENT_WITHIN),
        duration: durationJsonFormat,
      },
      {},
    );

  export interface OrderDate {
    type: typeof ORDER_DATE;
    start: Date | null;
    end: Date | null;
    dateOption: DateOption | null;
  }

  export interface ReturnDate {
    type: typeof RETURN_DATE;
    start: Date | null;
    end: Date | null;
    dateOption: DateOption | null;
  }

  export const orderDateJsonFormat: JsonFormat<OrderDate | ReturnDate> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(ORDER_DATE || RETURN_DATE),
        start: nullableJsonFormat(dateJsonFormat),
        end: nullableJsonFormat(dateJsonFormat),
        dateOption: nullableJsonFormat(
          stringJsonFormat as JsonFormat<DateOption>,
        ),
      },
      {},
    );

  export const returnDateJsonFormat: JsonFormat<ReturnDate> = objectJsonFormat(
    {
      type: symbolJsonFormat(RETURN_DATE),
      start: nullableJsonFormat(dateJsonFormat),
      end: nullableJsonFormat(dateJsonFormat),
      dateOption: nullableJsonFormat(
        stringJsonFormat as JsonFormat<DateOption>,
      ),
    },
    {},
  );

  export interface OrderWithin {
    type: typeof ORDER_WITHIN;
    duration: Duration;
  }

  export const orderWithinFormat: JsonFormat<OrderWithin> = objectJsonFormat(
    { type: symbolJsonFormat(ORDER_WITHIN), duration: durationJsonFormat },
    {},
  );

  export interface Price {
    type: typeof PRICE;
    amount: number;
    priceOption: PriceOption | null;
  }

  export const priceJsonFormat: JsonFormat<Price> = objectJsonFormat(
    {
      type: symbolJsonFormat(PRICE),
      amount: numberJsonFormat,
      priceOption: nullableJsonFormat(
        stringJsonFormat as JsonFormat<PriceOption>,
      ),
    },
    {},
  );

  export interface ProductTag {
    type: typeof PRODUCT_TAG;
    tags: readonly string[];
  }

  export const productTagJson: JsonFormat<ProductTag> = objectJsonFormat(
    {
      type: symbolJsonFormat(PRODUCT_TAG),
      tags: arrayJsonFormat(stringJsonFormat),
    },
    {},
  );

  export interface SalesChannel {
    type: typeof SALES_CHANNEL;
    channels: readonly string[];
  }

  export const salesChannelJson: JsonFormat<SalesChannel> = objectJsonFormat(
    {
      type: symbolJsonFormat(SALES_CHANNEL),
      channels: arrayJsonFormat(stringJsonFormat),
    },
    {},
  );

  export interface ShippingWithin {
    type: typeof DELIVERY_WITHIN;
    duration: Duration;
  }

  export const deliveryWithinJson: JsonFormat<ShippingWithin> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(DELIVERY_WITHIN),
        duration: durationJsonFormat,
      },
      {},
    );

  export interface CustomerCountry {
    type: typeof CUSTOMER_COUNTRY;
    countries: readonly string[];
  }

  export const customerCountryJsonFormat: JsonFormat<CustomerCountry> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(CUSTOMER_COUNTRY),
        countries: arrayJsonFormat(stringJsonFormat),
      },
      {},
    );
}

export const conditionJsonFormat: JsonFormat<Condition> = symbolUnionJsonFormat(
  "type",
  "type",
  {
    [Condition.CUSTOMER_TAG]: Condition.customerTagJsonFormat,
    [Condition.CUSTOMER_COUNTRY]: Condition.customerCountryJsonFormat,
    [Condition.DELIVERY_WITHIN]: Condition.deliveryWithinJson,
    [Condition.DISCOUNT_CODE]: Condition.discountCodeJsonFormat,
    [Condition.FULFILLMENT_WITHIN]: Condition.fulfillmentPeriodJsonFormat,
    [Condition.ORDER_DATE]: Condition.orderDateJsonFormat,
    [Condition.RETURN_DATE]: Condition.returnDateJsonFormat,
    [Condition.ORDER_WITHIN]: Condition.orderWithinFormat,
    [Condition.PRICE]: Condition.priceJsonFormat,
    [Condition.PRODUCT_TAG]: Condition.productTagJson,
    [Condition.SALES_CHANNEL]: Condition.salesChannelJson,
  },
);

export interface Parameters {
  discountCodes: string[];
  now: Instant;
  fulfilledOn: Instant;
  deliveredOn: Instant;
  orderedOn: Instant;
  customerTags: string[];
  customerCountry: string;
  price: number;
  productTags: string[];
  salesChannel: string;
}

const evaluateDate = (
  start: Date,
  end: Date | null,
  condition: DateOption,
  compareTo: Instant,
) => {
  switch (condition) {
    case DateOption.BEFORE:
      return compareTo.isBefore(nativeJs(endOfDay(start)).toInstant());
    case DateOption.AFTER:
      return compareTo.isAfter(nativeJs(startOfDay(start)).toInstant());
    case DateOption.BETWEEN:
      return (
        compareTo.isAfter(nativeJs(startOfDay(start)).toInstant()) &&
        compareTo.isBefore(nativeJs(endOfDay(end!)).toInstant())
      );
  }
};

export namespace Condition {
  export function evaluate(
    condition: Condition,
    parameters: Parameters,
  ): boolean {
    switch (condition.type) {
      case Condition.CUSTOMER_TAG:
        return condition.tags.some((tag) =>
          parameters.customerTags.includes(tag),
        );
      case Condition.DISCOUNT_CODE:
        return condition.codes.some((code) =>
          parameters.discountCodes.includes(code),
        );
      case Condition.FULFILLMENT_WITHIN:
        return parameters.now.isBefore(
          parameters.fulfilledOn.plus(condition.duration),
        );
      case Condition.ORDER_DATE:
        return evaluateDate(
          condition.start!,
          condition.end,
          condition.dateOption!,
          parameters.orderedOn,
        );
      case Condition.RETURN_DATE:
        return evaluateDate(
          condition.start!,
          condition.end,
          condition.dateOption!,
          parameters.now,
        );
      case Condition.ORDER_WITHIN:
        return parameters.now.isBefore(
          parameters.orderedOn.plus(condition.duration),
        );
      case Condition.PRICE:
        switch (condition.priceOption!) {
          case PriceOption.EQUALS:
            return parameters.price.toFixed(2) === condition.amount.toFixed(2);
          case PriceOption.LESS_THAN:
            return parameters.price < condition.amount;
          case PriceOption.GREATER_THAN:
            return parameters.price > condition.amount;
          case PriceOption.CENTS_EQUALS:
            // Just get the cents values from each.
            return (
              parameters.price.toFixed(2).slice(-2) ===
              condition.amount.toFixed(2).slice(-2)
            );
        }
      case Condition.PRODUCT_TAG:
        const productTags =
          parameters.productTags?.map((tag) => tag.toLowerCase()) || [];
        return condition.tags.some((tag) =>
          productTags.includes(tag.toLowerCase()),
        );
      case Condition.DELIVERY_WITHIN:
        return parameters.now.isBefore(
          parameters.deliveredOn.plus(condition.duration),
        );
      case Condition.SALES_CHANNEL:
        let found = false;
        SalesChannels.forEach((channel: Channel) => {
          if (condition.channels.includes(channel.id)) {
            channel.sourceNames.forEach((sourceName: string) => {
              if (parameters.salesChannel.startsWith(sourceName)) {
                found = true;
              }
            });
          }
        });
        return found;
      case Condition.CUSTOMER_COUNTRY:
        return (
          // If we don't have the country for some reason, we assume it's allowed.
          !parameters.customerCountry ||
          // Otherwise only allow if the country is in the list.
          condition.countries.includes(parameters.customerCountry)
        );
    }
  }
}
